import { useMemo, useState } from 'react';
import { newsList } from '@/mocks/news';
import { useSeo } from '@/hooks/useSeo';
import PageBanner from '@/components/base/PageBanner';
import NewsCard from '@/components/base/NewsCard';
import Pagination from '@/components/base/Pagination';
import Reveal from '@/components/base/Reveal';

const TYPE_OPTIONS = [
  { value: 'all', label: '全部类型' },
  { value: 'news', label: '新闻' },
  { value: 'announcement', label: '公告' },
  { value: 'event', label: '活动' },
];

const PAGE_SIZE = 6;

export default function News() {
  useSeo({
    title: '最新原神资讯 – 原神官方游戏站',
    description: '汇总原神最新游戏资讯、官方公告与活动情报，支持按日期与类型筛选，快速了解提瓦特每日动态。',
    keywords: '原神资讯,原神公告,原神新闻,原神活动,提瓦特动态',
    canonicalPath: '/news',
  });

  const [type, setType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    return newsList
      .filter((item) => (type === 'all' ? true : item.type === type))
      .filter((item) => (dateFrom ? item.date >= dateFrom : true))
      .filter((item) => (dateTo ? item.date <= dateTo : true))
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [type, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const reset = () => {
    setType('all');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const updateFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <div>
      <PageBanner
        kicker="NEWS CENTER"
        title="最新原神游戏资讯"
        subtitle="掌握版本前瞻、官方公告与活动情报，按发布日期与内容类型快速筛选你关心的内容。"
        crumbs={[{ label: '首页', to: '/' }, { label: '资讯' }]}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14 lg:px-8">
        <div className="mb-8 rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {TYPE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updateFilter(setType)(option.value)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                    type === option.value
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-foreground-500">
                <i className="ri-calendar-line text-sm"></i>
                从
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(event) => updateFilter(setDateFrom)(event.target.value)}
                  className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-700 outline-none focus:border-primary-400"
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-foreground-500">
                到
                <input
                  type="date"
                  value={dateTo}
                  onChange={(event) => updateFilter(setDateTo)(event.target.value)}
                  className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-700 outline-none focus:border-primary-400"
                />
              </label>
              <button
                type="button"
                onClick={reset}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          </div>
          <p className="mt-4 text-xs text-foreground-500">
            共找到 <span className="font-medium text-primary-700">{filtered.length}</span> 条内容
          </p>
        </div>

        {paged.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {paged.map((item, index) => (
              <Reveal key={item.id} delay={index * 60}>
                <NewsCard item={item} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-inbox-line text-2xl"></i>
            </span>
            <p className="mt-4 font-heading text-base font-bold text-foreground-900">没有找到符合条件的资讯</p>
            <p className="mt-1.5 text-sm text-foreground-500">试试调整筛选条件，或重置筛选查看全部内容。</p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}

        <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}